import { useMemo } from "react";
import { supabase } from "./supabase";
import { useCachedQuery } from "./useCachedQuery";

// useKidPetAnimations — reads the pet_animation_assets manifest
// (migration 0251) + resolves each row to a Supabase Storage
// public URL. Tiny query, public-read, cached 24h. The kid app
// uses this to feed the AR view a `{ key: url }` map for the
// per-card USDZ animations that stream from CDN instead of
// shipping in the iOS bundle.
//
// The bucket layout is:
//   kid_pet_animations/<animation_key>-v<version>.usdz
// e.g. kid_pet_animations/super-v1.usdz
//
// To add a new animation:
//   1. Drop the USDZ into the kid_pet_animations bucket via
//      Supabase Dashboard at the matching path.
//   2. INSERT a row into pet_animation_assets:
//        { animation_key: 'super', storage_path: 'super-v1.usdz',
//          version: 1 }
//   3. Every kid sees the chip light up within ~24h (cache TTL)
//      or instantly on a fresh app open.
//
// To re-version an existing animation:
//   1. Upload USDZ at new path: e.g. super-v2.usdz
//   2. UPDATE the existing row: storage_path = 'super-v2.usdz',
//      version = 2
//   3. iOS-side cache invalidates because the cache key includes
//      the version — clients re-download the new file.

const BUCKET = "kid_pet_animations";
const TTL_MS = 24 * 60 * 60 * 1000;

export function useKidPetAnimations() {
  const { data: rows, loading, refresh } = useCachedQuery({
    cacheKey: "kid_pet_animations:manifest",
    ttlMs:    TTL_MS,
    fetcher: async () => {
      const { data, error } = await supabase
        .from("pet_animation_assets")
        .select("animation_key, storage_path, version, size_bytes, updated_at");
      if (error) {
        console.warn("[kid-pet-anims] fetch failed:", error.message);
        return [];
      }
      return data || [];
    },
  });

  // Build the { animation_key: publicUrl } map. The Supabase
  // public-URL resolver is synchronous (just URL templating against
  // SUPABASE_URL) so this is cheap to memoize on rows identity.
  const urlByKey = useMemo(() => {
    const out = {};
    for (const row of rows || []) {
      if (!row?.animation_key || !row?.storage_path) continue;
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(row.storage_path);
      if (pub?.publicUrl) out[row.animation_key] = pub.publicUrl;
    }
    return out;
  }, [rows]);

  return { urlByKey, rows: rows || [], loading, refresh };
}
